import { Component, OnInit, ViewEncapsulation, OnDestroy } from "@angular/core";
import { AppState, stateGetter } from "../../store/state/app.state";
import { Store } from "@ngrx/store";
import {
  SetDisplay,
  SetHistoricalEstimates,
  SetPeriods,
  SetVisualizations,
  SetSourceMetaLoading,
} from "../../store/actions/right-panel-actions";
import {
  faInfoCircle,
  faFileAlt,
  faCalculator,
  faChartLine,
  faHistory,
  faCalendarAlt,
  faTh,
} from "@fortawesome/pro-solid-svg-icons";
import { faTable } from "@fortawesome/pro-light-svg-icons";
import {
  ActiveMexCut,
  GetSourceMetaParams,
  HistoricalEstimates,
  Periods,
  RevisionSourceDataModel,
  Visualizations,
} from "../../store/models/right-panel-models";
import { RightPanelService } from "../right-panel.service";
import { PageParams } from "../../store/models/navigation-settings.model";
import { Dictionary } from "../../store/models/common_types";
import { Subscription } from "rxjs";
import {
  VAIconRegistryService,
  calculator,
  calendar,
  chartLine,
  file,
  table,
  th,
} from "@va/core-library/icons";

declare function OnDefaultSetting(sectionName);

@Component({
  selector: "app-right-panel",
  templateUrl: "./right-panel.component.html",
  styleUrls: ["./right-panel.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class RightPanelComponent implements OnInit, OnDestroy {
  isCollapsed = true;
  includePage: Array<string> = ["mex", "revisions", "surprise"];
  showError = false;
  faInfoCircle = faInfoCircle;
  faFileAlt = faFileAlt;
  faCalculator = faCalculator;
  faChartLine = faChartLine;
  faHistory = faHistory;
  faCalendarAlt = faCalendarAlt;
  faTable = faTable;
  faTh = faTh;
  activeCut: string;
  pageName: string;
  sourcePanelData: Dictionary<any>;
  showOnlySourcePanelOnPages: Array<string> = ["revisions", "surprise"];
  ticker = "";
  refreshRightPanelData = false;
  isClear = false;
  pg = "";
  cut = "";
  sourceMetaSubscription: Subscription;
  pageParamSub: Subscription; // TODO - make common method to unsubscribe each subscrition.
  defaultPanels = {
    visualizations: ["std", "nmv", "sch"],
    analytics: ["std", "nmv", "sch", "sar"],
    sources: ["std", "nmv", "sch"],
    periods: ["std", "nmv", "sch", "sar"],
    historicalEstimates: ["std", "nmv", "sch", "sar"],
    inferredData: ["nmv"],
    accountingBasis: ["nmv"],
  };
  panelVisibility = {
    visualizations: false,
    analytics: false,
    sources: false,
    periods: false,
    historicalEstimates: false,
    inferredData: false,
    accountingBasis: false,
  };

  constructor(
    private store: Store<AppState>,
    private rightPanelService: RightPanelService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([
      table,
      chartLine,
      th,
      calendar,
      file,
      calculator,
    ]);
  }

  ngOnInit() {
    this.store.dispatch(new SetSourceMetaLoading());
    this.pageParamSub = this.store
      .select(stateGetter("navigationSettings", "pageParams"))
      .subscribe((navState: PageParams) => {
        const { pg, cut, ticker } = navState;
        this.pg = pg;
        this.cut = cut;
        this.ticker = ticker;
        if (pg && cut && ticker) {
          this.ticker = ticker;
          if (this.showOnlySourcePanelOnPages.includes(this.pageName)) {
            this.fetchRightPanelSettings(pg, cut, ticker);
          }
        }
        if (navState) {
          this.ticker = navState.ticker;
          this.pageName = navState.pageName;
          if (this.showOnlySourcePanelOnPages.includes(this.pageName)) {
            this.setSourcePanelData();
            for (const property in this.panelVisibility) {
              if (property === "sources") {
                this.panelVisibility[property] = true;
              } else {
                this.panelVisibility[property] = false;
              }
            }
          }
          if (this.pageName === "mex") {
            if (this.cut) {
              for (const property in this.panelVisibility) {
                this.panelVisibility[property] = this.defaultPanels[
                  property
                ].includes(this.cut.toLowerCase());
              }
            }
          }
        }
      });

    this.store
      .select(stateGetter("globalSubheader", "pageName"))
      .subscribe((data: string) => {
        this.pageName = data;
        const showRightPanel = this.includePage.includes(data);
        if (showRightPanel) {
          this.store.dispatch(new SetDisplay(true));
        } else {
          this.store.dispatch(new SetDisplay(false));
        }
      });

    this.sourceMetaSubscription = this.store
      .select(stateGetter("rightPanel", "sourceMeta"))
      .subscribe((data: GetSourceMetaParams) => {
        this.ticker = data.ticker;
        this.isClear = data.isClear;
        if (
          this.ticker !== undefined &&
          this.showOnlySourcePanelOnPages.includes(this.pageName)
        ) {
          this.setSourcePanelData();
        }
      });

    if (this.pageName === "mex") {
      this.store
        .select(stateGetter("rightPanel", "activeCut"))
        .subscribe((data: ActiveMexCut) => {
          this.activeCut = data.cut;
        });

      window.addEventListener("MEDataSet", (rpObj: any) => {
        if (rpObj.section !== "ccUpdated") {
          this.sourcePanelData = rpObj.detail.data.panels.find(
            (panel) => panel.name.toLowerCase() === "sources",
          );
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.sourceMetaSubscription) {
      this.sourceMetaSubscription.unsubscribe();
    }
    if (this.pageParamSub) {
      this.pageParamSub.unsubscribe();
    }
  }

  setSourcePanelData() {
    this.rightPanelService
      .getSourcePanelMeta(this.ticker, this.pageName, this.isClear)
      .subscribe((res: RevisionSourceDataModel) => {
        if (res && res["status"] === "success") {
          if (this.showOnlySourcePanelOnPages.includes(this.pageName)) {
            this.sourcePanelData = res.data.panels;
          }
        }
      });
  }

  fetchRightPanelSettings = (pg: string, cut: string, ticker: string) => {
    this.rightPanelService
      .getRightPanelDimensions(pg, cut, ticker)
      .subscribe((rightPanelState: any) => {
        this.populateHistoricalEstimates(rightPanelState);
        this.populateVisualizations(rightPanelState);
        this.fetchPeriodsMeta(pg, cut, ticker);
      });
  };

  populateHistoricalEstimates = (rightPanelState) => {
    const histEstimates = rightPanelState.panels[0];
    const payload: HistoricalEstimates = {
      changedDate: histEstimates.changed_date,
      clearDimension: histEstimates.clear_dimension,
      default: histEstimates.default,
      effectiveStart: histEstimates.effective_start,
      type: histEstimates.type,
      revisionDateList:
        histEstimates.revision_date_list.length === 0
          ? [null]
          : histEstimates.revision_date_list,
      values: histEstimates.values.length === 0 ? [null] : histEstimates.values,
    };

    this.store.dispatch(new SetHistoricalEstimates(payload));
  };

  populateVisualizations = (rightPanelState) => {
    const visualizations = rightPanelState.panels[4];
    const payload: Visualizations = {
      clearDimension: visualizations.clear_dimension,
      values: visualizations.values,
    };
    this.store.dispatch(new SetVisualizations(payload));
  };

  fetchPeriodsMeta = (pg: string, cut: string, ticker: string) => {
    this.rightPanelService
      .getPeriodsMeta(pg, cut, ticker)
      .subscribe((periodsMeta: any) => {
        const periods = periodsMeta.panels[0];
        const payload: Periods = {
          clearDimension: periods.clear_dimension,
          firstEstimatedYear: periods.f_est_y,
          from: periods.from,
          to: periods.to,
          interimType: periods.i_type,
          multiPeriod: periods.multi_period,
          periodType: periods.period_type,
          selectedFrom: periods.selected_from.slice(3, 7),
          selectedTo: periods.selected_to.slice(3, 7),
        };

        this.store.dispatch(new SetPeriods(payload));
      });
  };

  onClearAllPresetsClicked = () => {
    OnDefaultSetting("All");
  };
}
